import { AlertTriangle, PlugZap, RotateCw, ShieldOff } from 'lucide-react'
import type { ReactNode } from 'react'
import { asApiError } from '@/api/errors'
import { cn } from '@/lib/cn'
import { Button } from './ui/button'
import { SkeletonTable } from './ui/skeleton'

interface ErrorStateProps {
  error: unknown
  onRetry?: () => void
  className?: string
  compact?: boolean
}

/** Inline error for a failed load. Explains what happened and offers a retry. */
export function ErrorState({ error, onRetry, className, compact }: ErrorStateProps) {
  const e = asApiError(error)
  const Icon = e.isNetworkError ? PlugZap : e.isForbidden ? ShieldOff : AlertTriangle
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-8' : 'px-6 py-14',
        className,
      )}
    >
      <div className="mb-3 grid size-10 place-items-center rounded-lg border border-danger/25 bg-danger-soft text-danger">
        <Icon className="size-5" strokeWidth={1.75} />
      </div>
      <h3 className="text-base font-semibold text-ink">{e.title}</h3>
      {e.detail && <p className="mt-1 max-w-md text-sm text-ink-2">{e.detail}</p>}
      {e.requestId && <p className="mt-2 font-mono text-xs text-ink-3">Request ID {e.requestId}</p>}
      {onRetry && !e.isForbidden && (
        <Button className="mt-4" size="sm" icon={<RotateCw />} onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

interface QueryStateProps<T> {
  query: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown }
  /** Rendered while loading. Defaults to a table skeleton. */
  loading?: ReactNode
  /** Rendered when `isEmpty(data)` is true. */
  empty?: ReactNode
  isEmpty?: (data: T) => boolean
  children: (data: T) => ReactNode
  compact?: boolean
}

/**
 * Renders loading, error, empty, or data for a TanStack Query result.
 *
 *   <QueryState query={q} empty={<EmptyState … />} isEmpty={(d) => d.items.length === 0}>
 *     {(data) => …}
 *   </QueryState>
 */
export function QueryState<T>({
  query,
  loading,
  empty,
  isEmpty,
  children,
  compact,
}: QueryStateProps<T>) {
  if (query.isPending) return <>{loading ?? <SkeletonTable />}</>
  if (query.error || query.data === undefined)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} compact={compact} />
  if (empty && isEmpty?.(query.data)) return <>{empty}</>
  return <>{children(query.data)}</>
}
